import { useState, useRef } from 'react';
import EverTrue from 'app';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { ListboxSelect } from '@evertrue/et-components';
import { debounce } from '@evertrue/sdk';
import GlobalSearchBox from './components/global-search-box';
import GlobalSearchDropdown from './components/global-search-dropdown';
import useOutsideClick from 'hooks/use-outside-click';
import { useGlobalSearch } from './global-search-context';
import { getQueryOptions } from './config/global-search-config';
import { useGate } from 'components/is-gated';

const GlobalSearchController = ({ initialIndex = 0, className = '' }) => {
  const linkButtonRef = useRef(null);
  const searchBoxRef = useRef(null);
  const [isFocused, setIsFocused] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);

  const { query, queryType, setQueryType, setQuery, handleDebouncedSearch, clearSearch } = useGlobalSearch();
  const hasInteractions = useGate('ugc_show_imported_notes');
  const QUERY_OPTIONS = getQueryOptions(hasInteractions);

  const clearSearchBox = () => {
    if (searchBoxRef.current) {
      searchBoxRef.current.value = '';
      // Trigger any necessary updates in the search box component
      const event = new Event('change', { bubbles: true });
      searchBoxRef.current.dispatchEvent(event);
    }
  };

  const handleOutsideClick = (evnt) => {
    if (!isFocused) {
      if (evnt) {
        $(evnt.currentTarget).blur();
      }
      clearSearch();
      clearSearchBox();
    }
  };

  useOutsideClick(searchBoxRef, handleOutsideClick);

  const handleKeyPress = async (value = '', evnt) => {
    switch (evnt.key) {
      case 'Enter': {
        // Use current input value directly to avoid race condition with debounced state
        const currentQuery = value.replace(/[/]/g, ' ').trim();
        setIsNavigating(true);
        handleGlobalSearchWithParams(currentQuery);
        break;
      }
      case 'Escape':
        handleOutsideClick();
        break;
      case 'Shift':
        return false;
      default: {
        const newQuery = value.replace(/[/]/g, ' ').trim();
        setIsNavigating(false); // Reset navigation flag when typing
        setQuery(newQuery);
        await handleDebouncedSearch(newQuery, queryType);
        return undefined;
      }
    }
  };

  const handleGlobalSearchWithParams = (searchQuery) => {
    clearSearch();
    clearSearchBox();
    EverTrue.Navigator(
      `/global-search/results?query=${encodeURIComponent(searchQuery)}&type=${encodeURIComponent(queryType)}`,
      {
        trigger: true,
        replace: false,
      }
    );
  };

  const handleGlobalSearch = (evnt) => {
    handleGlobalSearchWithParams(query);
  };

  const icon = QUERY_OPTIONS[queryType].icon;

  return (
    <div className="global-search-bar">
      <ListboxSelect
        className="global-search-box--type-select"
        emptyValue="All"
        label="Search by category..."
        icon={icon}
        disabled={!!searchBoxRef.current?.value}
        options={Object.keys(QUERY_OPTIONS).map((key) => ({
          value: QUERY_OPTIONS[key].value,
          label: QUERY_OPTIONS[key].name,
        }))}
        onChange={(selected) => setQueryType(selected.value)}
        value={QUERY_OPTIONS[queryType]}
        size="small"
      />
      <GlobalSearchBox
        searchRef={searchBoxRef}
        className={classNames('global-search--input', {
          'is-inactive': !(isFocused || query),
        })}
        onKeyUp={handleKeyPress}
        onChange={debounce((newQuery = '') => setQuery(newQuery), 200)}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        queryType={queryType}
        setQueryType={setQueryType}
        name="global-search-box"
      />

      {query && !isNavigating && (
        <GlobalSearchDropdown
          linkButtonRef={linkButtonRef}
          handleGlobalSearch={handleGlobalSearch}
          handleOutsideClick={handleOutsideClick}
        />
      )}
    </div>
  );
};

export default GlobalSearchController;

GlobalSearchController.propTypes = {
  initialIndex: PropTypes.number,
  className: PropTypes.string,
};
